import React from 'react';
import {
  Card,
  CardContent,
  CardActions,
  Box,
  Typography,
  Chip,
  IconButton,
  Button,
  Grid,
  Alert,
  Tooltip,
  Stack,
  useTheme,
  useMediaQuery
} from '@mui/material';
import { SxProps, Theme } from '@mui/material/styles';
import {
  CalendarToday,
  AccessTime,
  Assignment,
  LocationOn,
  Person,
  Notes,
  MoreVert,
  // CheckCircleOutline,
  Description,
  ErrorOutline,
  InsertDriveFile
} from '@mui/icons-material';
import InsertPhotoIcon from '@mui/icons-material/InsertPhoto';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import GetAppIcon from '@mui/icons-material/GetApp';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { format, isToday } from 'date-fns';
import toast from 'react-hot-toast';
import { InspectionCardProps, Inspection } from './types';

/**
 * InspectionCard - Responsive card component for displaying inspection details
 */
const InspectionCard: React.FC<InspectionCardProps> = ({
  inspection,
  // handleOpenCompletionDialog,
  handleViewDetails,
  handleActionMenuOpen,
  selectedInspection,
  setSelectedInspection,
  // handleUpdateStatus
  handleStartInspection
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  // const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  // Status colors mapping
  const statusColors = {
    Scheduled: 'primary',
    InProgress: 'warning',
    Pass: 'success',
    Fail: 'error',
    cancelled: 'default'
  } as const;

  // Get inspection date indicator styles
  const getDateIndicatorStyle = (inspection1: Inspection): SxProps<Theme> => {
    const today = format(new Date(), 'yyyy-MM-dd');
    const tomorrow = format(new Date(new Date().setDate(new Date().getDate() + 1)), 'yyyy-MM-dd');

    if (inspection1.inspectionDate === today) {
      return { bgcolor: 'error.main', color: 'white', borderRadius: 1, px: 1, py: 0.5 };
    } else if (inspection1.inspectionDate === tomorrow) {
      return { bgcolor: 'warning.main', color: 'white', borderRadius: 1, px: 1, py: 0.5 };
    } else if (inspection1.status === 'Pass') {
      return { bgcolor: 'success.light', color: 'white', borderRadius: 1, px: 1, py: 0.5 };
    } else {
      return { bgcolor: 'action.selected', borderRadius: 1, px: 1, py: 0.5 };
    }
  };

  // Replace the handleButtonClick function (around line 65)
  const handleStartButtonClick = () => {
    handleStartInspection(inspection);
  };

  // Responsive configuration
  const getResponsiveConfig = () => ({
    cardPadding: isMobile ? 2 : 3,
    titleVariant: isMobile ? 'subtitle1' : 'h6',
    bodyVariant: isMobile ? 'body2' : 'body2',
    captionVariant: isMobile ? 'caption' : 'body2',
    iconSize: isMobile ? 'small' : 'small',
    chipSize: isMobile ? 'small' : 'small',
    buttonSize: isMobile ? 'small' : 'medium',
    spacing: isMobile ? 1 : 1.5
  });

  const config = getResponsiveConfig();

  // Render info item (reusable component)
  const InfoItem = ({
    icon,
    label,
    value,
    highlight = false
  }: {
    icon: React.ReactNode;
    label: string;
    value: string | React.ReactNode;
    highlight?: boolean;
  }) => (
    <Box sx={{
      display: 'flex',
      alignItems: 'flex-start',
      gap: { xs: 1, sm: 1.5 },
      mb: { xs: 1.5, sm: 1 }
    }}>
      <Box sx={{
        mt: 0.25,
        minWidth: { xs: 20, sm: 24 }
      }}>
        {icon}
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          variant={config.captionVariant as any}
          fontWeight="medium"
          sx={{
            mb: 0.25,
            fontSize: { xs: '0.75rem', sm: '0.875rem' }
          }}
        >
          {label}
        </Typography>
        {highlight ? (
          <Box
            component="span"
            sx={{
              display: 'inline-block',
              fontSize: { xs: '0.8rem', sm: '0.875rem' },
              lineHeight: 1.4,
              ...getDateIndicatorStyle(inspection)
            }}
          >
            {value}
          </Box>
        ) : (
          <Typography
            variant={config.bodyVariant as any}
            color="text.secondary"
            sx={{
              fontSize: { xs: '0.8rem', sm: '0.875rem' },
              lineHeight: 1.4,
              wordBreak: 'break-word'
            }}
          >
            {value}
          </Typography>
        )}
      </Box>
    </Box>
  );

  return (
    <Card
      key={inspection.id}
      sx={{
        mb: { xs: 1.5, md: 2 },
        border: (inspection.status === 'Scheduled' || inspection.status === 'Rescheduled') && isToday(new Date(inspection.inspectionDate)) ?
          '1px solid #f44336' : 'none',
        boxShadow: (inspection.status === 'Scheduled' || inspection.status === 'Rescheduled') && isToday(new Date(inspection.inspectionDate)) ?
          '0 0 8px rgba(244, 67, 54, 0.3)' : { xs: 1, md: 2 },
        borderRadius: { xs: 1, md: 2 }
      }}
    >
      <CardContent sx={{
        p: { xs: 2, sm: 2.5, md: 3 },
        '&:last-child': { pb: { xs: 2, sm: 2.5, md: 3 } }
      }}>
        {/* Header Section */}
        <Box sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'flex-start', sm: 'flex-start' },
          mb: { xs: 2, md: 1 },
          gap: { xs: 1, sm: 2 }
        }}>
          <Typography
            variant={config.titleVariant as any}
            component="div"
            sx={{
              fontWeight: 600,
              fontSize: { xs: '1rem', sm: '1.1rem', md: '1.25rem' },
              lineHeight: 1.3,
              flex: { xs: 'none', sm: 1 },
              minWidth: 0,
              wordBreak: 'break-word'
            }}
          >
            {inspection.applicationTitle}
          </Typography>

          <Box sx={{
            display: 'flex',
            flexDirection: { xs: 'row', sm: 'row' },
            flexWrap: 'wrap',
            gap: { xs: 0.75, sm: 1 },
            alignItems: 'center',
            width: { xs: '100%', sm: 'auto' },
            justifyContent: { xs: 'space-between', sm: 'flex-end' }
          }}>
            <Box sx={{
              display: 'flex',
              gap: { xs: 0.5, sm: 1 },
              flexWrap: 'wrap'
            }}>
              <Chip
                label={inspection.status.charAt(0).toUpperCase() + inspection.status.slice(1)}
                color={statusColors[inspection.status]}
                size={config.chipSize as any}
                sx={{
                  height: { xs: 24, sm: 28 },
                  fontSize: { xs: '0.7rem', sm: '0.75rem' },
                  fontWeight: 500
                }}
              />
              {inspection.priority !== 'Normal' && (
                <Chip
                  label={inspection.priority}
                  color={inspection.priority === 'Urgent' ? 'error' : 'warning'}
                  size={config.chipSize as any}
                  sx={{
                    height: { xs: 24, sm: 28 },
                    fontSize: { xs: '0.7rem', sm: '0.75rem' },
                    fontWeight: 500
                  }}
                />
              )}
            </Box>

            <IconButton
              size={config.iconSize as any}
              onClick={(e) => handleActionMenuOpen(e, inspection)}
              disabled={(inspection.status === 'Pass' || inspection.status === 'Fail' || inspection.status === 'Not Completed')}
              sx={{
                ml: { xs: 'auto', sm: 0 },
                minWidth: { xs: 40, sm: 44 },
                height: { xs: 40, sm: 44 }
              }}
            >
              <MoreVert />
            </IconButton>
          </Box>
        </Box>

        {/* Application Number */}
        <Typography
          variant="body2"
          color="text.secondary"
          gutterBottom
          sx={{
            fontSize: { xs: '0.8rem', sm: '0.875rem' },
            mb: { xs: 2, md: 1.5 }
          }}
        >
          Application #: {inspection.applicationNumber}
        </Typography>

        {/* Main Content Grid */}
        <Grid container spacing={{ xs: 2, md: 3 }} sx={{ mt: 0 }}>
          <Grid item xs={12} md={6}>
            <InfoItem
              icon={<CalendarToday fontSize={config.iconSize as any} color="primary" />}
              label="Inspection Date"
              value={
                <>
                  {format(new Date(inspection.inspectionDate), 'MMM dd, yyyy')}
                  {isToday(new Date(inspection.inspectionDate)) && ' (TODAY)'}
                </>
              }
              highlight={true}
            />

            <InfoItem
              icon={<AccessTime fontSize={config.iconSize as any} color="primary" />}
              label="Time Slot"
              value={inspection.timeSlot}
            />

            <InfoItem
              icon={<Assignment fontSize={config.iconSize as any} color="primary" />}
              label="Inspection Type"
              value={inspection.inspectionType}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <InfoItem
              icon={<LocationOn fontSize={config.iconSize as any} color="primary" />}
              label="Property Address"
              value={inspection.propertyAddress}
            />

            <InfoItem
              icon={<Person fontSize={config.iconSize as any} color="primary" />}
              label="Contact Information"
              value={`${inspection.contactName} • ${inspection.contactPhone}`}
            />

            {inspection.notes && (
              <InfoItem
                icon={<Notes fontSize={config.iconSize as any} color="primary" />}
                label="Notes"
                value={inspection.notes}
              />
            )}
          </Grid>
        </Grid>

        {/* Completion Details Section */}
        {(inspection.status === 'Pass' || inspection.status === 'Fail' || inspection.status === 'Not Completed') && (
          <Box sx={{
            mt: { xs: 2, md: 2 },
            pt: { xs: 2, md: 2 },
            borderTop: '1px dashed #e0e0e0'
          }}>
            <Typography
              variant="body2"
              fontWeight="medium"
              gutterBottom
              sx={{ fontSize: { xs: '0.875rem', md: '0.875rem' } }}
            >
              {inspection.status === 'Pass' ? 'Completion Details:' :
                inspection.status === 'Fail' ? 'Failure Details:' :
                  'Incomplete Details:'}
            </Typography>

            {inspection.completedAt && (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  fontSize: { xs: '0.8rem', md: '0.875rem' },
                  mb: 1
                }}
              >
                {inspection.status === 'Pass' ? 'Completed on: ' :
                  inspection.status === 'Fail' ? 'Failed on: ' :
                    'Last updated on: '}
                {format(new Date(inspection.completedAt), 'MMM dd, yyyy hh:mm a')}
              </Typography>
            )}

            {inspection.inspectionNote && (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  fontSize: { xs: '0.8rem', md: '0.875rem' },
                  mb: 1,
                  wordBreak: 'break-word'
                }}
              >
                {inspection.status === 'Pass' ? 'Notes: ' :
                  inspection.status === 'Fail' ? 'Failure Reason: ' :
                    'Reason: '}
                {inspection.inspectionNote}
              </Typography>
            )}

            {inspection.completionNotes && inspection.completionNotes !== inspection.inspectionNote && (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  fontSize: { xs: '0.8rem', md: '0.875rem' },
                  mb: 1,
                  wordBreak: 'break-word'
                }}
              >
                Additional Notes: {inspection.completionNotes}
              </Typography>
            )}

            {inspection.attachments?.length > 0 && (
              <Box sx={{ mt: 1.5 }}>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    fontSize: { xs: '0.8rem', md: '0.875rem' },
                    mb: 1
                  }}
                >
                  Attachments:
                </Typography>
                <Box sx={{
                  display: 'flex',
                  gap: { xs: 0.5, md: 1 },
                  flexWrap: 'wrap'
                }}>
                  {inspection.attachments.map(attachment => (
                    <Chip
                      key={attachment.id}
                      label={attachment.name}
                      size="small"
                      icon={<InsertDriveFile fontSize="small" />}
                      onClick={() => { }}
                      sx={{
                        height: { xs: 24, md: 28 },
                        fontSize: { xs: '0.7rem', md: '0.75rem' },
                        maxWidth: { xs: '100%', md: 'none' }
                      }}
                    />
                  ))}
                </Box>
              </Box>
            )}
          </Box>
        )}

        {/* Expanded Details Section */}
        {selectedInspection?.id === inspection.id && (
          <Box sx={{
            mt: { xs: 2, md: 2 },
            pt: { xs: 2, md: 2 },
            borderTop: '1px solid #e0e0e0'
          }}>
            <Alert
              severity="info"
              sx={{
                mb: 2,
                '& .MuiAlert-message': {
                  fontSize: { xs: '0.8rem', md: '0.875rem' }
                }
              }}
            >
              <Typography
                variant="body2"
                fontWeight="medium"
                sx={{ fontSize: { xs: '0.8rem', md: '0.875rem' } }}
              >
                View full details for each inspection before conducting the inspection.
              </Typography>
            </Alert>

            <Typography
              variant="body2"
              fontWeight="medium"
              gutterBottom
              sx={{ fontSize: { xs: '0.875rem', md: '0.875rem' } }}
            >
              Additional Information:
            </Typography>

            <Typography
              variant="body2"
              color="text.secondary"
              paragraph
              sx={{
                fontSize: { xs: '0.8rem', md: '0.875rem' },
                lineHeight: 1.5
              }}
            >
              This inspection was requested on {format(new Date(2023, 5, 15), 'MMM dd, yyyy')} and scheduled on {format(new Date(2023, 5, 20), 'MMM dd, yyyy')}.
            </Typography>

            <Typography
              variant="body2"
              color="text.secondary"
              paragraph
              sx={{
                fontSize: { xs: '0.8rem', md: '0.875rem' },
                wordBreak: 'break-word'
              }}
            >
              Subcode: {inspection.subcode}
            </Typography>

            <Typography
              variant="body2"
              color="text.secondary"
              fontWeight="medium"
              paragraph
              sx={{ fontSize: { xs: '0.8rem', md: '0.875rem' } }}
            >
              You are required to check the following aspects:
            </Typography>

            <Typography
              component="div"
              variant="body2"
              color="text.secondary"
              sx={{
                fontSize: { xs: '0.8rem', md: '0.875rem' },
                '& ul': {
                  pl: { xs: 2, md: 3 },
                  m: 0
                },
                '& li': {
                  mb: 0.5
                }
              }}
            >
              <ul>
                {inspection?.inspectionCheckList && inspection.inspectionCheckList.length > 0 ? (
                  inspection.inspectionCheckList.map((item, index) => (
                    <li key={index}>{item.Name}</li>
                  ))
                ) : (
                  <li>No checklist items available</li>
                )}
              </ul>
            </Typography>

            {/* Documents Section */}
            {inspection.documents && inspection.documents.length > 0 && (
              <>
                <Typography
                  variant="body2"
                  fontWeight="medium"
                  gutterBottom
                  sx={{
                    mt: 2,
                    fontSize: { xs: '0.875rem', md: '0.875rem' }
                  }}
                >
                  Uploaded Documents:
                </Typography>
                <Stack spacing={1} sx={{ mt: 1, mb: 2 }}>
                  {inspection.documents.map((document, index) => {
                    const fileName = document.DocumentPath.split('/').pop() || `Document ${index + 1}`;
                    const isImage = /\.(jpg|jpeg|png|gif|bmp|webp)$/i.test(fileName);

                    return (
                      <Box
                        key={index}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          p: { xs: 1, md: 1.5 },
                          borderRadius: 1,
                          bgcolor: 'background.paper',
                          border: '1px solid #e0e0e0',
                          gap: { xs: 1, md: 1.5 }
                        }}
                      >
                        {isImage ? (
                          <InsertPhotoIcon sx={{
                            color: 'primary.main',
                            fontSize: { xs: '1.2rem', md: '1.5rem' }
                          }} />
                        ) : (
                          <InsertDriveFileIcon sx={{
                            color: 'primary.main',
                            fontSize: { xs: '1.2rem', md: '1.5rem' }
                          }} />
                        )}

                        <Typography
                          variant="body2"
                          sx={{
                            flex: 1,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            fontSize: { xs: '0.8rem', md: '0.875rem' },
                            lineHeight: 1.4
                          }}
                        >
                          {fileName}
                        </Typography>

                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          <Tooltip title="View">
                            <IconButton
                              size="small"
                              onClick={() => window.open(document.DocumentUrl, '_blank')}
                              sx={{
                                minWidth: { xs: 32, md: 36 },
                                height: { xs: 32, md: 36 }
                              }}
                            >
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>

                          <Tooltip title="Download">
                            <IconButton
                              size="small"
                              component="a"
                              href={document.DocumentUrl}
                              download={fileName}
                              sx={{
                                minWidth: { xs: 32, md: 36 },
                                height: { xs: 32, md: 36 }
                              }}
                            >
                              <GetAppIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </Box>
                    );
                  })}
                </Stack>
              </>
            )}

            <Typography
              variant="body2"
              fontWeight="medium"
              gutterBottom
              sx={{
                mt: 2,
                fontSize: { xs: '0.875rem', md: '0.875rem' }
              }}
            >
              Recommendations:
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ fontSize: { xs: '0.8rem', md: '0.875rem' } }}
            >
              Access the full application details before proceeding with the inspection.
            </Typography>
          </Box>
        )}
      </CardContent>

      {/* Action Buttons */}

      <CardActions sx={{
        px: { xs: 2, md: 2 },
        pb: { xs: 2, md: 2 },
        pt: 0,
        display: 'flex',
        flexDirection: { xs: 'column', sm: 'row' },
        gap: { xs: 1, sm: 1 },
        '& > :not(style) ~ :not(style)': {
          marginLeft: 0
        },
        '& .MuiButton-root': {
          justifyContent: 'center',
          margin: 0
        },
        alignItems: { xs: 'stretch', sm: 'center' },
        justifyContent: { xs: 'stretch', sm: 'flex-start' }
      }}>
        <Box sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          gap: { xs: 1, sm: 1 },
          flex: { xs: 'none', sm: 1 },
          width: { xs: '100%', sm: 'auto' }
        }}>
          {/* Show buttons for Scheduled status */}
          {(inspection.status === 'Scheduled' || inspection.status === 'Rescheduled') && (
            <>
              {/* <Button
          variant="contained"
          color="success"
          startIcon={<CheckCircleOutline />}
          onClick={() => handleOpenCompletionDialog(inspection)}
          sx={{
            fontSize: { xs: '0.8rem', md: '0.875rem' },
            py: { xs: 1, md: 0.75 },
            flex: { xs: '1', sm: 'none' }
          }}
        >
          Mark as Complete
        </Button> */}
              <Button
                variant="outlined"
                color="primary"
                startIcon={<AccessTime />}
                onClick={handleStartButtonClick}
                size={config.buttonSize as any}
                sx={{
                  fontSize: { xs: '0.8rem', md: '0.875rem' },
                  py: { xs: 1, md: 0.75 },
                  flex: { xs: '1', sm: 'none' }
                }}
              >
                Start Inspection
              </Button>
            </>
          )}

          {/* Show buttons for InProgress status */}
          {inspection.status === 'In Progress' && (
            <>
              {/* <Button
          variant="contained"
          color="success"
          startIcon={<CheckCircleOutline />}
          onClick={() => handleOpenCompletionDialog(inspection)}
          size={config.buttonSize as any}
          sx={{
            fontSize: { xs: '0.8rem', md: '0.875rem' },
            py: { xs: 1, md: 0.75 },
            flex: { xs: '1', sm: 'none' }
          }}
        >
          Complete
        </Button> */}
              <Button
                variant="outlined"
                color="warning"
                startIcon={<AccessTime />}
                onClick={handleStartButtonClick}
                size={config.buttonSize as any}
                sx={{
                  fontSize: { xs: '0.8rem', md: '0.875rem' },
                  py: { xs: 1, md: 0.75 },
                  flex: { xs: '1', sm: 'none' }
                }}
              >
                Continue Inspection
              </Button>
            </>
          )}

          {/* Show reschedule button for issue status */}
          {inspection.status === 'issue' && (
            <Button
              variant="outlined"
              color="error"
              startIcon={<ErrorOutline />}
              onClick={() => {
                toast.success('Rescheduling process initiated');
              }}
              size={config.buttonSize as any}
              sx={{
                fontSize: { xs: '0.8rem', md: '0.875rem' },
                py: { xs: 1, md: 0.75 },
                flex: { xs: '1', sm: 'none' }
              }}
            >
              Reschedule
            </Button>
          )}
        </Box>

        {/* View Details Button - Always show except for completed statuses */}
        {!['Pass', 'Fail', 'Not Completed'].includes(inspection.status) && (
          <Button
            variant="outlined"
            startIcon={<Description />}
            onClick={() => selectedInspection?.id === inspection.id ?
              setSelectedInspection(null) :
              handleViewDetails(inspection)
            }
            size={config.buttonSize as any}
            sx={{
              fontSize: { xs: '0.8rem', md: '0.875rem' },
              py: { xs: 1, md: 0.75 },
              flex: { xs: '1', sm: 'none' },
              alignSelf: { xs: 'stretch', sm: 'center' },
            }}
          >
            {selectedInspection?.id === inspection.id ? 'Hide Details' : 'View Details'}
          </Button>
        )}

        {/* View Details Button for completed inspections */}
        {['Pass', 'Fail', 'Not Completed'].includes(inspection.status) && (
          <Button
            variant="outlined"
            startIcon={<Description />}
            onClick={() => selectedInspection?.id === inspection.id ?
              setSelectedInspection(null) :
              handleViewDetails(inspection)
            }
            size={config.buttonSize as any}
            sx={{
              fontSize: { xs: '0.8rem', md: '0.875rem' },
              py: { xs: 1, md: 0.75 },
              flex: { xs: '1', sm: 'none' },
              alignSelf: { xs: 'stretch', sm: 'center' },
            }}
          >
            {selectedInspection?.id === inspection.id ? 'Hide Details' : 'View Details'}
          </Button>
        )}
      </CardActions>
    </Card>
  );
};

export default InspectionCard;